// mindmetal-app/web/portal/src/pages/employee/mindmetal/SignalsTab.tsx
import { useState, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Brain,
  Activity,
  Shield,
  Zap,
  Pencil,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Users,
  RotateCcw,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, api } from '@personic/shared'
import { SignalGauge } from '@/components/mindmetal/SignalGauge'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { TrackedValue } from '@personic/shared'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SignalTier = 'universal' | 'behavioral' | 'regulated' | 'pragmatic'

interface TestUserOption {
  canonical_id: string
  display_name: string
  profile_id: string
  coverage_level: string
}

interface ProfileResponse {
  id: number
  profile_id: string
  canonical_id: string
  signals_universal: Record<string, unknown>
  signals_behavioral: Record<string, unknown>
  signals_regulated: Record<string, unknown>
  signals_pragmatic: Record<string, unknown>
  [key: string]: unknown
}

// Signal category definitions per tier
const TIER_CATEGORIES: Record<SignalTier, { key: string; label: string; signals: { key: string; label: string; leftLabel?: string; rightLabel?: string }[] }[]> = {
  universal: [
    {
      key: 'personality',
      label: 'Big Five Personality',
      signals: [
        { key: 'openness', label: 'Openness', leftLabel: 'Conventional', rightLabel: 'Open' },
        { key: 'conscientiousness', label: 'Conscientiousness', leftLabel: 'Flexible', rightLabel: 'Disciplined' },
        { key: 'extraversion', label: 'Extraversion', leftLabel: 'Introverted', rightLabel: 'Extraverted' },
        { key: 'agreeableness', label: 'Agreeableness', leftLabel: 'Challenging', rightLabel: 'Agreeable' },
        { key: 'neuroticism', label: 'Neuroticism', leftLabel: 'Stable', rightLabel: 'Sensitive' },
      ],
    },
    {
      key: 'communication_style',
      label: 'Communication Style',
      signals: [
        { key: 'formality', label: 'Formality', leftLabel: 'Casual', rightLabel: 'Formal' },
        { key: 'verbosity', label: 'Verbosity', leftLabel: 'Concise', rightLabel: 'Verbose' },
        { key: 'directness', label: 'Directness', leftLabel: 'Indirect', rightLabel: 'Direct' },
        { key: 'technicalDepth', label: 'Technical Depth', leftLabel: 'Simple', rightLabel: 'Technical' },
        { key: 'emotionalExpressiveness', label: 'Emotional Expressiveness', leftLabel: 'Reserved', rightLabel: 'Expressive' },
        { key: 'humorAppreciation', label: 'Humor Appreciation', leftLabel: 'Serious', rightLabel: 'Humorous' },
      ],
    },
    {
      key: 'cognitive_style',
      label: 'Cognitive Style',
      signals: [
        { key: 'detailPreference', label: 'Detail Preference', leftLabel: 'Overview', rightLabel: 'Detailed' },
        { key: 'pace', label: 'Pace', leftLabel: 'Slower', rightLabel: 'Faster' },
        { key: 'structurePreference', label: 'Structure Preference', leftLabel: 'Free-form', rightLabel: 'Structured' },
        { key: 'abstractionLevel', label: 'Abstraction Level', leftLabel: 'Concrete', rightLabel: 'Abstract' },
      ],
    },
  ],
  behavioral: [
    {
      key: 'preferences',
      label: 'Preferences',
      signals: [
        { key: 'risk_tolerance', label: 'Risk Tolerance', leftLabel: 'Risk Averse', rightLabel: 'Risk Seeking' },
        { key: 'novelty_seeking', label: 'Novelty Seeking', leftLabel: 'Familiar', rightLabel: 'Novel' },
        { key: 'social_orientation', label: 'Social Orientation', leftLabel: 'Individual', rightLabel: 'Social' },
        { key: 'decision_speed', label: 'Decision Speed', leftLabel: 'Deliberate', rightLabel: 'Spontaneous' },
        { key: 'feedback_receptivity', label: 'Feedback Receptivity', leftLabel: 'Resistant', rightLabel: 'Receptive' },
      ],
    },
    {
      key: 'interaction_dynamics',
      label: 'Interaction Dynamics',
      signals: [
        { key: 'initiative', label: 'Initiative', leftLabel: 'Reactive', rightLabel: 'Proactive' },
        { key: 'collaboration_style', label: 'Collaboration Style', leftLabel: 'Independent', rightLabel: 'Collaborative' },
        { key: 'conflict_approach', label: 'Conflict Approach', leftLabel: 'Avoidant', rightLabel: 'Confrontational' },
        { key: 'trust_building', label: 'Trust Building', leftLabel: 'Cautious', rightLabel: 'Open' },
      ],
    },
  ],
  regulated: [
    {
      key: 'clinical',
      label: 'Clinical',
      signals: [
        { key: 'stress_resilience', label: 'Stress Resilience', leftLabel: 'Low', rightLabel: 'High' },
        { key: 'emotional_regulation', label: 'Emotional Regulation', leftLabel: 'Low', rightLabel: 'High' },
        { key: 'self_awareness', label: 'Self Awareness', leftLabel: 'Low', rightLabel: 'High' },
        { key: 'adaptability', label: 'Adaptability', leftLabel: 'Low', rightLabel: 'High' },
      ],
    },
    {
      key: 'talent',
      label: 'Talent',
      signals: [
        { key: 'leadership_potential', label: 'Leadership Potential', leftLabel: 'Low', rightLabel: 'High' },
        { key: 'team_orientation', label: 'Team Orientation', leftLabel: 'Low', rightLabel: 'High' },
        { key: 'growth_mindset', label: 'Growth Mindset', leftLabel: 'Fixed', rightLabel: 'Growth' },
        { key: 'accountability', label: 'Accountability', leftLabel: 'Low', rightLabel: 'High' },
      ],
    },
  ],
  pragmatic: [
    {
      key: 'epistemic_markers',
      label: 'Epistemic Markers',
      signals: [
        { key: 'certainty', label: 'Certainty', leftLabel: 'Uncertain', rightLabel: 'Certain' },
        { key: 'evidence_reliance', label: 'Evidence Reliance', leftLabel: 'Intuitive', rightLabel: 'Evidence-based' },
        { key: 'source_diversity', label: 'Source Diversity', leftLabel: 'Narrow', rightLabel: 'Diverse' },
        { key: 'critical_thinking', label: 'Critical Thinking', leftLabel: 'Accepting', rightLabel: 'Analytical' },
      ],
    },
    {
      key: 'commitment_patterns',
      label: 'Commitment Patterns',
      signals: [
        { key: 'follow_through', label: 'Follow Through', leftLabel: 'Low', rightLabel: 'High' },
        { key: 'promise_calibration', label: 'Promise Calibration', leftLabel: 'Over-promise', rightLabel: 'Under-promise' },
        { key: 'consistency', label: 'Consistency', leftLabel: 'Variable', rightLabel: 'Consistent' },
        { key: 'urgency_response', label: 'Urgency Response', leftLabel: 'Delayed', rightLabel: 'Immediate' },
      ],
    },
  ],
}

const TIER_META: { key: SignalTier; label: string; icon: React.ElementType; color: string }[] = [
  { key: 'universal', label: 'Universal', icon: Brain, color: 'copper' },
  { key: 'behavioral', label: 'Behavioral', icon: Activity, color: 'copper' },
  { key: 'regulated', label: 'Regulated', icon: Shield, color: 'copper' },
  { key: 'pragmatic', label: 'Pragmatic', icon: Zap, color: 'copper' },
]

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve a TrackedValue from nested JSONB, handling both camelCase and snake_case keys */
function resolveSignal(
  tierData: Record<string, unknown>,
  categoryKey: string,
  signalKey: string,
): TrackedValue | undefined {
  const category = tierData[categoryKey] as Record<string, unknown> | undefined
  if (!category) return undefined

  // Try direct key first, then snake_case fallback
  const snakeKey = signalKey.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)
  const raw = category[signalKey] ?? category[snakeKey]
  if (!raw || typeof raw !== 'object') return undefined

  const obj = raw as Record<string, unknown>
  if (typeof obj.value === 'number') {
    return {
      value: obj.value,
      confidence: typeof obj.confidence === 'number' ? obj.confidence : 0,
      sensitivityLevel: obj.sensitivityLevel as TrackedValue['sensitivityLevel'],
      lastUpdated: obj.lastUpdated as string | undefined,
    }
  }
  return undefined
}

/** Count how many signals exist in a tier */
function countTierSignals(tierData: Record<string, unknown>, tier: SignalTier): number {
  let count = 0
  for (const category of TIER_CATEGORIES[tier]) {
    for (const signal of category.signals) {
      if (resolveSignal(tierData, category.key, signal.key)) {
        count++
      }
    }
  }
  return count
}

/** Build a flat editable map from tier data */
function buildEditMap(tierData: Record<string, unknown>, tier: SignalTier): Record<string, { value: number; confidence: number }> {
  const map: Record<string, { value: number; confidence: number }> = {}
  for (const category of TIER_CATEGORIES[tier]) {
    for (const signal of category.signals) {
      const resolved = resolveSignal(tierData, category.key, signal.key)
      const mapKey = `${category.key}.${signal.key}`
      map[mapKey] = {
        value: resolved?.value ?? 0.5,
        confidence: resolved?.confidence ?? 0,
      }
    }
  }
  return map
}

/** Convert edit map back to nested JSONB for PATCH */
function editMapToPayload(editMap: Record<string, { value: number; confidence: number }>): Record<string, Record<string, TrackedValue>> {
  const result: Record<string, Record<string, TrackedValue>> = {}
  for (const [compositeKey, val] of Object.entries(editMap)) {
    const [category, signal] = compositeKey.split('.')
    if (!result[category]) result[category] = {}
    result[category][signal] = {
      value: val.value,
      confidence: val.confidence,
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SignalsTab() {
  const queryClient = useQueryClient()

  // State
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [activeTier, setActiveTier] = useState<SignalTier>('universal')
  const [editMode, setEditMode] = useState(false)
  const [editMap, setEditMap] = useState<Record<string, { value: number; confidence: number }>>({})
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Fetch test users for dropdown
  const { data: testUsersData, isLoading: usersLoading } = useQuery({
    queryKey: ['mindmetal-test-users', { page: 1, limit: 100 }],
    queryFn: async () => {
      const { data } = await api.get<{ test_users: TestUserOption[] }>(
        '/api/mindmetal/v3/admin/test-users?page=1&limit=100'
      )
      return data
    },
  })

  // Fetch profile for selected user
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['mindmetal-profile', selectedUserId],
    queryFn: async () => {
      const { data } = await api.get<ProfileResponse>(
        `/api/mindmetal/v3/admin/test-users/${selectedUserId}/profile`
      )
      return data
    },
    enabled: !!selectedUserId,
  })

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ profileId, tier, payload }: { profileId: string; tier: SignalTier; payload: Record<string, unknown> }) => {
      const { data } = await api.patch(
        `/admin/mindmetal/profiles/${profileId}/signals/${tier}`,
        payload
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mindmetal-profile', selectedUserId] })
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      setEditMode(false)
    },
  })

  // Derived data
  const tierData = useMemo(() => {
    if (!profile) return {} as Record<string, unknown>
    const tierKeyMap: Record<SignalTier, string> = {
      universal: 'signals_universal',
      behavioral: 'signals_behavioral',
      regulated: 'signals_regulated',
      pragmatic: 'signals_pragmatic',
    }
    return (profile[tierKeyMap[activeTier]] || {}) as Record<string, unknown>
  }, [profile, activeTier])

  const tierSignalCount = useMemo(() => {
    if (!profile) return 0
    return countTierSignals(tierData, activeTier)
  }, [profile, tierData, activeTier])

  const totalCategorySignals = useMemo(() => {
    return TIER_CATEGORIES[activeTier].reduce((sum, cat) => sum + cat.signals.length, 0)
  }, [activeTier])

  // Handlers
  const handleUserChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserId(e.target.value)
    setEditMode(false)
    setEditMap({})
    setExpandedCategories(new Set())
  }, [])

  const handleEnterEdit = useCallback(() => {
    setEditMap(buildEditMap(tierData, activeTier))
    setEditMode(true)
    // Expand all categories when entering edit mode
    setExpandedCategories(new Set(TIER_CATEGORIES[activeTier].map((c) => c.key)))
  }, [tierData, activeTier])

  const handleCancelEdit = useCallback(() => {
    setEditMode(false)
    setEditMap({})
  }, [])

  const handleSave = useCallback(() => {
    if (!profile) return
    const payload = editMapToPayload(editMap)
    saveMutation.mutate({
      profileId: profile.profile_id,
      tier: activeTier,
      payload,
    })
  }, [profile, editMap, activeTier, saveMutation])

  const handleEditValue = useCallback((compositeKey: string, field: 'value' | 'confidence', raw: string) => {
    const num = parseFloat(raw)
    if (isNaN(num)) return
    const clamped = Math.min(1, Math.max(0, num))
    setEditMap((prev) => ({
      ...prev,
      [compositeKey]: {
        ...prev[compositeKey],
        [field]: clamped,
      },
    }))
  }, [])

  const handleResetSignal = useCallback((compositeKey: string) => {
    setEditMap((prev) => ({
      ...prev,
      [compositeKey]: { value: 0.5, confidence: 0 },
    }))
  }, [])

  const toggleCategory = useCallback((key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const handleTierChange = useCallback((tier: SignalTier) => {
    setActiveTier(tier)
    setEditMode(false)
    setEditMap({})
    setExpandedCategories(new Set())
  }, [])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div {...fadeUp} transition={{ duration: 0.3 }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-midnight-800">Signal Inspector</h3>
            <p className="text-sm text-warm-500">
              Browse and edit signal values across all profile tiers
            </p>
          </div>

          {/* User selector */}
          <div className="flex items-center gap-3">
            <Users className="w-4 h-4 text-warm-400 flex-shrink-0" />
            <select
              value={selectedUserId}
              onChange={handleUserChange}
              className="block w-64 rounded-lg border border-warm-200 bg-white px-3 py-2 text-sm text-midnight-800 shadow-sm focus:border-copper-500 focus:ring-1 focus:ring-copper-500 transition-colors"
            >
              <option value="">Select a test user...</option>
              {testUsersData?.test_users.map((u) => (
                <option key={u.canonical_id} value={u.canonical_id}>
                  {u.display_name} ({u.canonical_id})
                </option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Loading state for users */}
      {usersLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
        </div>
      )}

      {/* No user selected */}
      {!selectedUserId && !usersLoading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Brain className="w-12 h-12 text-warm-300 mx-auto mb-3" />
            <p className="text-warm-500">Select a test user to inspect their signal profile.</p>
            <p className="text-warm-400 text-sm mt-1">
              Signals are organized into four tiers: Universal, Behavioral, Regulated, and Pragmatic.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Profile loading */}
      {selectedUserId && profileLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-600" />
            <span className="text-warm-500 text-sm">Loading profile signals...</span>
          </div>
        </div>
      )}

      {/* Profile error */}
      {selectedUserId && profileError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">
              Failed to load profile for this user. They may not have a profile yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Success toast */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed top-6 right-6 z-50 flex items-center gap-2 bg-sage-50 border border-sage-300 text-sage-700 px-4 py-3 rounded-xl shadow-lg"
          >
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium">Signals saved successfully</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile loaded -- show tiers */}
      {selectedUserId && profile && !profileLoading && (
        <>
          {/* Tier selector bar */}
          <motion.div {...fadeUp} transition={{ duration: 0.35, delay: 0.05 }}>
            <div className="flex flex-wrap items-center gap-2">
              {TIER_META.map((tier) => {
                const Icon = tier.icon
                const isActive = activeTier === tier.key
                const profileTierKey = `signals_${tier.key}` as keyof ProfileResponse
                const data = (profile[profileTierKey] || {}) as Record<string, unknown>
                const hasData = Object.keys(data).length > 0
                const signalCount = countTierSignals(data, tier.key)

                return (
                  <button
                    key={tier.key}
                    onClick={() => handleTierChange(tier.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-copper-100 text-copper-800 border border-copper-300 shadow-sm'
                        : hasData
                          ? 'bg-warm-50 text-midnight-700 border border-warm-200 hover:border-warm-300 hover:bg-warm-100'
                          : 'bg-warm-50 text-warm-400 border border-warm-100 hover:border-warm-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tier.label}
                    {signalCount > 0 && (
                      <Badge
                        variant={isActive ? 'copper' : 'default'}
                        size="sm"
                      >
                        {signalCount}
                      </Badge>
                    )}
                  </button>
                )
              })}

              {/* Edit toggle */}
              <div className="ml-auto flex items-center gap-2">
                {editMode ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      leftIcon={<X className="w-4 h-4" />}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      isLoading={saveMutation.isPending}
                      leftIcon={<Save className="w-4 h-4" />}
                    >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEnterEdit}
                    leftIcon={<Pencil className="w-4 h-4" />}
                  >
                    Edit Signals
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Save error */}
          {saveMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Failed to save signals: {(saveMutation.error as Error)?.message || 'Unknown error'}
            </div>
          )}

          {/* Tier summary */}
          <motion.div {...fadeUp} transition={{ duration: 0.35, delay: 0.1 }}>
            <div className="flex items-center gap-4 text-sm text-warm-500">
              <span>
                <span className="font-medium text-midnight-700">{tierSignalCount}</span> of{' '}
                <span className="font-medium text-midnight-700">{totalCategorySignals}</span> signals populated
              </span>
              <div className="flex-1 h-1.5 bg-warm-100 rounded-full overflow-hidden max-w-xs">
                <motion.div
                  className="h-full bg-gradient-to-r from-copper-400 to-copper-600 rounded-full"
                  initial={{ width: 0 }}
                  animate={{
                    width: totalCategorySignals > 0
                      ? `${(tierSignalCount / totalCategorySignals) * 100}%`
                      : '0%',
                  }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
            </div>
          </motion.div>

          {/* Signal categories */}
          <div className="space-y-4">
            {TIER_CATEGORIES[activeTier].map((category, catIdx) => {
              const isExpanded = expandedCategories.has(category.key) || !editMode
              const categorySignals = category.signals
              const populatedCount = categorySignals.filter(
                (s) => resolveSignal(tierData, category.key, s.key) !== undefined
              ).length

              return (
                <motion.div
                  key={`${activeTier}-${category.key}`}
                  {...fadeUp}
                  transition={{ duration: 0.35, delay: 0.05 * catIdx }}
                >
                  <Card>
                    {/* Category header */}
                    <button
                      onClick={() => toggleCategory(category.key)}
                      className="w-full"
                    >
                      <CardHeader className="cursor-pointer hover:bg-warm-50/50 transition-colors">
                        <div className="flex items-center justify-between w-full">
                          <CardTitle className="flex items-center gap-2 text-base">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-warm-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-warm-400" />
                            )}
                            <span className="capitalize">
                              {category.label}
                            </span>
                          </CardTitle>
                          <Badge variant={populatedCount > 0 ? 'copper' : 'default'} size="sm">
                            {populatedCount}/{categorySignals.length}
                          </Badge>
                        </div>
                      </CardHeader>
                    </button>

                    {/* Category signals */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <CardContent className="pt-0">
                            <div className={editMode ? 'space-y-5' : 'space-y-4'}>
                              {categorySignals.map((signal) => {
                                const compositeKey = `${category.key}.${signal.key}`
                                const resolved = resolveSignal(tierData, category.key, signal.key)

                                if (editMode) {
                                  const editValues = editMap[compositeKey] || { value: 0.5, confidence: 0 }

                                  return (
                                    <div
                                      key={compositeKey}
                                      className="p-4 bg-warm-50 rounded-xl space-y-3"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-midnight-800 text-sm">
                                          {signal.label}
                                        </span>
                                        <button
                                          onClick={() => handleResetSignal(compositeKey)}
                                          className="flex items-center gap-1 text-xs text-warm-400 hover:text-copper-600 transition-colors"
                                          title="Reset to default"
                                        >
                                          <RotateCcw className="w-3 h-3" />
                                          Reset
                                        </button>
                                      </div>

                                      <div className="grid grid-cols-2 gap-4">
                                        {/* Value input */}
                                        <div>
                                          <label className="block text-xs text-warm-500 mb-1">
                                            Value ({signal.leftLabel} - {signal.rightLabel})
                                          </label>
                                          <div className="flex items-center gap-2">
                                            <input
                                              type="range"
                                              min={0}
                                              max={1}
                                              step={0.01}
                                              value={editValues.value}
                                              onChange={(e) => handleEditValue(compositeKey, 'value', e.target.value)}
                                              className="flex-1 accent-copper-600 h-2"
                                            />
                                            <input
                                              type="number"
                                              min={0}
                                              max={1}
                                              step={0.01}
                                              value={editValues.value.toFixed(2)}
                                              onChange={(e) => handleEditValue(compositeKey, 'value', e.target.value)}
                                              className="w-16 text-center text-sm border border-warm-200 rounded-lg px-2 py-1 bg-white focus:border-copper-500 focus:ring-1 focus:ring-copper-500"
                                            />
                                          </div>
                                        </div>

                                        {/* Confidence input */}
                                        <div>
                                          <label className="block text-xs text-warm-500 mb-1">
                                            Confidence
                                          </label>
                                          <div className="flex items-center gap-2">
                                            <input
                                              type="range"
                                              min={0}
                                              max={1}
                                              step={0.01}
                                              value={editValues.confidence}
                                              onChange={(e) => handleEditValue(compositeKey, 'confidence', e.target.value)}
                                              className="flex-1 accent-copper-600 h-2"
                                            />
                                            <input
                                              type="number"
                                              min={0}
                                              max={1}
                                              step={0.01}
                                              value={editValues.confidence.toFixed(2)}
                                              onChange={(e) => handleEditValue(compositeKey, 'confidence', e.target.value)}
                                              className="w-16 text-center text-sm border border-warm-200 rounded-lg px-2 py-1 bg-white focus:border-copper-500 focus:ring-1 focus:ring-copper-500"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {/* Mini preview gauge */}
                                      <div className="pt-1">
                                        <SignalGauge
                                          label=""
                                          value={{ value: editValues.value, confidence: editValues.confidence }}
                                          leftLabel={signal.leftLabel}
                                          rightLabel={signal.rightLabel}
                                        />
                                      </div>
                                    </div>
                                  )
                                }

                                // Read-only view
                                return (
                                  <SignalGauge
                                    key={compositeKey}
                                    label={signal.label}
                                    value={resolved}
                                    leftLabel={signal.leftLabel}
                                    rightLabel={signal.rightLabel}
                                  />
                                )
                              })}
                            </div>

                            {/* Empty state for category */}
                            {!editMode && populatedCount === 0 && (
                              <div className="text-center py-6 text-warm-400 text-sm">
                                No signals populated in this category yet.
                              </div>
                            )}
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              )
            })}
          </div>

          {/* Bottom save bar (sticky) when in edit mode */}
          <AnimatePresence>
            {editMode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="sticky bottom-4 z-10"
              >
                <div className="flex items-center justify-between bg-midnight-900 text-white rounded-2xl px-6 py-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <Pencil className="w-4 h-4 text-copper-400" />
                    <span className="text-sm">
                      Editing <span className="font-semibold capitalize">{activeTier}</span> signals
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="text-warm-300 hover:text-white"
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      isLoading={saveMutation.isPending}
                      leftIcon={<Save className="w-4 h-4" />}
                    >
                      Save {activeTier} Signals
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  )
}
